import Phaser from "phaser";
import { animationFrameAt, clockElapsed, pauseClock, resumeClock, RUNNING_CLOCK, type AnimationClock, type AssetManifest, type FrameAnimation } from "daedalus-core";
import { pageTextureKey } from "./atlas.ts";

/**
 * Drives animated frames (ADR-041): every registered Phaser image shows the frame its animation reaches
 * on one shared clock, so cells and props sharing an animation stay in lockstep. `setFrame` runs only when
 * an animation's frame index changes, so an idle tick costs one comparison per animation. While paused (P1.6-T2)
 * the shared clock stands still and an update does nothing, and a resume carries on from the frame it stopped on.
 */

/**
 * One animation's followers and the frame they last showed.
 */
interface Followers {
  animation: FrameAnimation;
  readonly images: Set<Phaser.GameObjects.Image>;
  index: number;
}

/**
 * The animator.
 */
export class TileAnimator {
  private readonly groups = new Map<string, Followers>();
  private clock: AnimationClock = RUNNING_CLOCK;
  private elapsed = 0;
  private lastNowMs = 0;

  /**
   * @param manifest Asset manifest, for the animations and page textures.
   */
  public constructor(private readonly manifest: AssetManifest) {}

  /**
   * Whether a frame starts a looping animation. A transition animation (`once`, ADR-058) never runs on its
   * own, so a cell or prop placed with its first frame shows that frame.
   *
   * @param frameId The frame.
   * @returns True when the manifest loops it.
   */
  public animates(frameId: string): boolean {
    const animation = this.manifest.animations[frameId];
    return animation !== undefined && animation.once !== true;
  }

  /**
   * Registers an image to follow a frame's looping animation. The image shows the animation's current
   * frame at once.
   *
   * @param frameId The frame the image was placed with.
   * @param image The image.
   * @throws Error when the frame starts no animation or only a transition animation.
   */
  public follow(frameId: string, image: Phaser.GameObjects.Image): void {
    const animation = this.manifest.animations[frameId];
    if (animation === undefined || animation.once === true) {
      throw new Error(`frame ${frameId} starts no looping animation`);
    }
    let group = this.groups.get(frameId);
    if (group === undefined) {
      group = { animation, images: new Set(), index: animationFrameAt(animation, this.elapsed) };
      this.groups.set(frameId, group);
    }
    group.images.add(image);
    this.show(image, animation, group.index);
  }

  /**
   * Stops an image following its animation; destroying the image is the caller's job.
   *
   * @param frameId The frame it followed.
   * @param image The image.
   */
  public unfollow(frameId: string, image: Phaser.GameObjects.Image): void {
    const group = this.groups.get(frameId);
    if (group === undefined) {
      return;
    }
    group.images.delete(image);
    if (group.images.size === 0) {
      this.groups.delete(frameId);
    }
  }

  /**
   * Takes up a frame's animation again from the manifest, after its durations or how it plays changed there (a
   * strip's timing saved, ADR-107 decision 21): the images following it show the frame the new durations reach on
   * the shared clock at once. When the frame no longer starts a looping animation, they show its first frame and
   * follow nothing. A frame nothing follows changes nothing: an image placed with a strip that has just become a
   * loop follows it when it is next drawn.
   *
   * @param frameId The frame whose animation changed.
   */
  public retime(frameId: string): void {
    const group = this.groups.get(frameId);
    if (group === undefined) {
      return;
    }
    const animation = this.manifest.animations[frameId];
    if (animation === undefined || animation.once === true) {
      for (const image of group.images) {
        this.show(image, group.animation, 0);
      }
      this.groups.delete(frameId);
      return;
    }
    group.animation = animation;
    group.index = animationFrameAt(animation, this.elapsed);
    for (const image of group.images) {
      this.show(image, animation, group.index);
    }
  }

  /**
   * Pauses or resumes the shared clock; asking for the state it is in changes nothing. A pause stops the clock at
   * the last update's time, so the frames on screen are the frames it holds, and a resume carries on from there.
   *
   * @param paused Whether the animations stand still.
   * @param nowMs The wall time a resume takes effect, on the clock `update` is given.
   */
  public setPaused(paused: boolean, nowMs: number): void {
    this.clock = paused ? pauseClock(this.clock, this.lastNowMs) : resumeClock(this.clock, nowMs);
  }

  /**
   * Whether the shared clock stands still.
   *
   * @returns True while paused.
   */
  public get paused(): boolean {
    return this.clock.pausedAt !== null;
  }

  /**
   * Advances every animation to the frame the clock reaches; while paused, to none and at no cost.
   *
   * @param nowMs Wall time in milliseconds.
   * @returns How many images changed frame.
   */
  public update(nowMs: number): number {
    if (this.clock.pausedAt !== null) {
      return 0;
    }
    const elapsedMs = clockElapsed(this.clock, nowMs);
    this.elapsed = elapsedMs;
    this.lastNowMs = nowMs;
    let changed = 0;
    for (const group of this.groups.values()) {
      const index = animationFrameAt(group.animation, elapsedMs);
      if (index === group.index) {
        continue;
      }
      group.index = index;
      for (const image of group.images) {
        this.show(image, group.animation, index);
        changed += 1;
      }
    }
    return changed;
  }

  /**
   * How many images are being animated.
   *
   * @returns The count.
   */
  public get count(): number {
    let n = 0;
    for (const group of this.groups.values()) {
      n += group.images.size;
    }
    return n;
  }

  /**
   * Shows one animation frame on an image.
   *
   * @param image The image.
   * @param animation The animation.
   * @param index Frame index.
   */
  private show(image: Phaser.GameObjects.Image, animation: FrameAnimation, index: number): void {
    const frame = animation.frames[index] as string;
    const rect = this.manifest.frames[frame];
    if (rect === undefined) {
      return;
    }
    image.setTexture(pageTextureKey(this.manifest, rect.page), frame);
  }
}
